<template>
  <div
    class="track-button-wrapper relative size-17"
    v-if="!mobileMode"
    ref="trackButtonWrapperEl"
  >
    <div
      :class="`track-button
        ${showMore ? '' : ' hidden'}
        ${activeTrackingTask ? ' active' : ''}`"
        @mouseenter="showMore ? debounceClose(false) : null"
        @mouseleave="showMore ? debounceClose(true) : null"
      >
      <!-- one element for both states, so its colour fades in step with the expander tab -->
      <SimpleButton
        v-if="activeTrackingTask || taskToResume"
        variant="ghost"
        class="header-button flex! size-17 flex-col justify-center gap-0.75 text-center shadow-none"
        :title="activeTrackingTask ? null : `${$t('menu.Resume')} [alt+t]`"
        @click="onTrackClick"
        @shortkey="() => !activeTrackingTask && resume()"
        @contextmenu="onTrackContextMenu"
      >
        <template v-if="activeTrackingTask">
          <IconPauseCircle16Solid class="text-xl mb-1"></IconPauseCircle16Solid>
          <div class="label text-label font-bold leading-none">Pause</div>
        </template>
        <template v-else>
          <IconPlayCircle16Solid class="text-xl mb-1"></IconPlayCircle16Solid>
          <div class="label text-label font-bold leading-none">Resume</div>
        </template>
      </SimpleButton>

      <div class="expander-wrapper ml-[0.5px]" v-if="taskToResume">
        <div class="expander" >
          <div class="flex flex-col shrink-0 expander-in" style="gap:1px; background: var(--line)">
            <div class="task-item tracked" @click="openTaskDialog">
              <div class="task-item-info">
                <div class="project-stage">
                  <span>{{taskToResume.project ? taskToResume.project.name : ''}}</span>
                  <span>{{taskToResume.stage ? taskToResume.stage.name : ''}}</span>
                </div>
                <div class="task-text">{{taskToResume.textMD}}</div>
              </div>
              <span v-show="false">{{ trackingTimer }}</span>
              <i class="tr tr-open-window"></i>
            </div>
            <RouterLink
              v-for="t in recentTasksRelevant"
              @click="startTask(t)"
              :to="taskUrl(t)"
              :key="`rt-${t.id}`" class="task-item">
              <img class="probg" :src="getTaskBg(t)" />
              <div class="task-item-info">
                <div class="project-stage">
                  <span>{{t.project ? t.project.name : ''}}</span>
                  <span>{{t.stage ? t.stage.name : ''}}</span>
                </div>
                <div class="task-text">{{t.textMD}}</div>
              </div>
              <span v-show="false">{{ t }}</span>
              <div class="taskprev"
                :style="{ backgroundColor: mainAttachmentBg(t) }"
              >
                <img
                  v-if="mainAttachmentUrl(t)"
                  :src="mainAttachmentUrl(t)"/>
              </div>

              <UserAvatar withDayoffBadge
                class="ava"
                :user='t.assigneeId && getUser(t.assigneeId)'
              />
            </RouterLink>
            <RouterLink
              :to="taskHistoryRoute"
              class="flex items-center justify-center gap-1 rounded-b-[6px] border-l border-line bg-bg2 py-2
                     text-label font-medium text-text-muted transition-colors duration-150 hover:text-accent"
              @click="showMore = false"
            >
              {{ $t('menu.view_all_task_history') }}
              <IconArrowRight class="size-3.5" />
            </RouterLink>
          </div>
          <div class="show-more" @click="showMore = !showMore">
            <span class="triangle"></span>
          </div>
        </div>
      </div>
    </div>

    <Transition name="pause-options">
      <div class="pause-options" v-if="showPauseOptions && activeTrackingTask">
        <div class="pause-option-item" @click="pauseUntilWindowActivation">
          <IconWindow16Solid class="pause-option-icon" />
          {{ $t('menu.PauseUntilWindowActivation') }}
        </div>
        <div class="pause-option-item" @click="pauseForMinutes(5)">
          <IconClock16Solid class="pause-option-icon" />
          {{ $t('menu.PauseFor5Minutes') }}
        </div>
        <div class="pause-option-item" @click="pauseForMinutes(10)">
          <IconClock16Solid class="pause-option-icon" />
          {{ $t('menu.PauseFor10Minutes') }}
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { beautySlug, lastTrackedTaskOfOpenWorkspace, saveTaskToLocalStorage } from '@/store/utils';
import { useStore } from 'vuex';
import { EVENT__AUTOCLICK } from '@/utils/constantHolder';
import dayjs from 'dayjs';
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useMagicKeys, onClickOutside } from '@vueuse/core';
import { IconPauseCircle16Solid, IconPlayCircle16Solid, IconClock16Solid, IconWindow16Solid } from '@iconify-prerendered/vue-heroicons';
import { IconArrowRight } from '@iconify-prerendered/vue-tabler';
import SimpleButton from './SimpleButton.vue';


const mobileMode = computed(() => store.state.system.mobileMode);


const MILLISECONDS_IN_SECOND = 1000;

const keys = useMagicKeys();

watch(keys['alt+t'], (pressed) => {
  if (pressed) {
    const taskToResume = props.taskUnderEdit || recentlyTrackedTask.value;
    if (taskToResume) {
      emit('start', { task: taskToResume, manual: true });
    }
  }
});


// Props
const props = defineProps({
  activeTrackingTask: Object,
  taskUnderEdit: Object,
});

// Emits
const emit = defineEmits(['start', 'stop']);

// Store
const store = useStore();

// Reactive data
const recentlyTrackedTask = ref(null);
const showMore = ref(false);
const closeTimer = ref(null);
const needsAutoclick = ref(false);
const startPoint = ref(null);
const showPauseOptions = ref(false);
const trackButtonWrapperEl = ref(null);

onClickOutside(trackButtonWrapperEl, () => { showPauseOptions.value = false; });

const closePauseOnEsc = (e) => { if (e.key === 'Escape') showPauseOptions.value = false; };
onMounted(() => document.addEventListener('keydown', closePauseOnEsc));
onBeforeUnmount(() => document.removeEventListener('keydown', closePauseOnEsc));

const pauseUntilWindowActivation = () => {
  showPauseOptions.value = false;
  emit('stop', { trigEvent: 'pause', manual: true, pauseUntil: 'windowActivation' });
};

const pauseForMinutes = (minutes) => {
  showPauseOptions.value = false;
  emit('stop', { trigEvent: 'pause', manual: true, pauseForMinutes: minutes });
};

// Store state and getters
const user = computed(() => store.state.user.user);
const nowUTC = computed(() => store.state.system.nowUTC);
const recentTasks = computed(() => store.state.user.recentTasks);
const projectsList = computed(() => store.state.project.projectsList);
const taskHistoryRoute = computed(() => ({
  name: 'taskHistory',
  params: {
    projectSlug: store.state.project.project.slug || 'last',
    slugNameProject: store.state.project.project.nameSlug || 'Last Project',
  },
}));
const getUser = (id) => store.getters['user/getUser']?.(id);  // optional chaining

// Computed properties
const taskUrl = computed(() => {
  return t => ({
    name: 'projectTask',
    params: {
      ...(t.project && t.project.name && t.project.slug
        ? {
          projectSlug: t.project.slug,
            slugNameProject: t.project.nameSlug,
        }
        : {}),
      hid: t.hid,
      taskSlug: t.slug,
      slugNameTask: beautySlug({
        text: t.textMD || 'image',
        lower: true,
        length: 30,
        sliceLength: 15,
      }, 'untitled-task'),
    },
  });
});

const mainAttachmentUrl = computed(() => {
  return (t) => {
    const preview = t.attachments && t.attachments.find(a => a.type === 'TASK_PREVIEW');
    if (!preview) {
      return null;
    }
    return `${window.s3base}/attachments/${preview.workspaceId}/${t.projectId}`
      + `/${t.id}/${preview.id}.${preview.extension}`;
  };
});

const mainAttachmentBg = computed(() => {
  return (t) => {
    const preview = t.attachments && t.attachments.find(a => a.type === 'TASK_PREVIEW');
    if (!preview) {
      return null;
    }
    return preview.dominantColor || 'grey';
  };
});

const recentTasksRelevant = computed(() => {
  if (!recentTasks.value) {
    return [];
  }
  return recentTasks.value.filter(t => t && t.id !== taskToResume.value.id && t.id !== props.activeTrackingTask?.id);
});

const taskToResume = computed(() => {
  return props.taskUnderEdit || recentlyTrackedTask.value;
});

const getTaskBg = computed(() => {
  return (task) => {
    if (task.projectId && projectsList.value) {
      const project = projectsList.value.find(p => p?.id === task.projectId);
      if (project && project.backgroundShort) {
        return project.backgroundShort;
      }
    }
    return null;
  };
});

const userStageRules = computed(() => {
  if (props.activeTrackingTask && props.activeTrackingTask.project) {
    const projectRules = JSON.parse(props.activeTrackingTask.project.rules);
    return projectRules?.find(r => r.stageId === props.activeTrackingTask.stageId)?.buttons;
  }
  return null;
});

const autoClickRule = computed(() => {
  return userStageRules.value?.primary?.find(r => r.action === EVENT__AUTOCLICK);
});

const autoClickAfter = computed(() => {
  return (autoClickRule.value?.duration || 0) * 60;
});

const userTTBU = computed(() => {
  return props.activeTrackingTask?.taskTrackedByUsers?.find(t => t.userId === user.value.id);
});

const baseTimeSeconds = computed(() => {
  let time = userTTBU.value ? (userTTBU.value.totalSeconds || 0) : -1;
  if (userTTBU.value && userTTBU.value.timeSlots && userTTBU.value.timeSlots.length) {
    userTTBU.value.timeSlots.forEach((item) => {
      time += item.actualTimeSeconds;
    });
  }
  return time;
});

const trackingTimer = computed(() => {
  let time = baseTimeSeconds.value;
  if (autoClickAfter.value && props.activeTrackingTask && needsAutoclick.value) {
    const diffInMilliseconds = dayjs.utc(nowUTC.value) - dayjs.utc(startPoint.value);
    const totalTime = diffInMilliseconds + (baseTimeSeconds.value * MILLISECONDS_IN_SECOND);
    time = totalTime / MILLISECONDS_IN_SECOND;
    if (needsAutoclick.value && autoClickAfter.value <= time) {
      runAutoclickRule();
    }
  }
  const tParsed = Math.max(time, 0);
  return Math.floor(tParsed);
});

// Methods
const startTask = (task) => {
  emit('start', { task });
};

const resume = () => {
  emit('start', { task: taskToResume.value, manual: true });
};

const onTrackClick = () => {
  if (props.activeTrackingTask) {
    emit('stop', { trigEvent: 'pause', manual: true });
    return;
  }
  resume();
};

const onTrackContextMenu = (e) => {
  if (!props.activeTrackingTask) return;
  e.preventDefault();
  showPauseOptions.value = !showPauseOptions.value;
};

const openTaskDialog = () => {
  showMore.value = false;
  if (!(recentlyTrackedTask.value && props.taskUnderEdit && recentlyTrackedTask.value.id === props.taskUnderEdit.id)) {
    store.commit('project/mutationOpenTaskPopup', { task: recentlyTrackedTask.value });
  }
};

const debounceClose = (start) => {
  if (start) {
    closeTimer.value = setTimeout(() => { showMore.value = false; }, 1000);
  } else {
    clearTimeout(closeTimer.value);
    closeTimer.value = null;
  }
};

const runAutoclickRule = () => {
  needsAutoclick.value = false;

  console.debug('Triggering autoclick rule for task:');
  console.debug(JSON.stringify(props.activeTrackingTask));
  console.debug('With user stage rules:');
  console.debug(JSON.stringify(userStageRules.value));
  store.dispatch('project/actionRunRules', {
    task: props.activeTrackingTask,
    stageRules: userStageRules.value,
    type: 'primary',
  });
};

// Lifecycle
onMounted(() => {
  if (!props.activeTrackingTask) {
    recentlyTrackedTask.value = lastTrackedTaskOfOpenWorkspace();
  }
  startPoint.value = nowUTC.value;
});

// Watchers
watch(() => props.activeTrackingTask, (newValue, oldValue) => {
  if (newValue?.id !== oldValue?.id) {
    startPoint.value = nowUTC.value;
  }
  if (newValue) {
    recentlyTrackedTask.value = newValue;
    saveTaskToLocalStorage(newValue);
  }
}, { immediate: true });

watch(baseTimeSeconds, (value) => {
  if (value) {
    needsAutoclick.value = autoClickAfter.value >= value;
  }
});

watch(() => props.taskUnderEdit, (value) => {
  if (value) {
    showMore.value = false;
    if (!props.activeTrackingTask) {
      recentlyTrackedTask.value = value;
      saveTaskToLocalStorage(value);
    }
  }
}, { immediate: true });
</script>


<style lang="scss" scoped>
@media screen and (max-width: 600px) {
  .track-button-wrapper[class] {
    position: fixed;
    right: 10px;
    bottom: 60px;
    height: 68px;
    width: 68px;
    .track-button[class] {
      border-radius: 50%;
      width: 100%;
      height: 100%;
      .header-button {
        .label {
          display: none;
        }
      }
      .expander {
        display: none;
      }
    }
  }
}

.track-button[class] {
  position: absolute;
  pointer-events: none;
  left: 0;
  top: 0;
  bottom: 0;
  display: flex;
  height: max-content;
  overflow: hidden;
  border-radius: var(--tile-radius);
  transition: max-height 0s;

  color: var(--text);
  border-color: var(--line);

  .header-button {
    pointer-events: all;
    border-radius: var(--tile-radius) 0 0 var(--tile-radius);
    z-index: 1;
    background: var(--neutral);
    border: 0;
    color: var(--on-neutral);
    transition: background-color 0.3s, color 0.3s, filter 0.3s;
    &:hover {
      filter: brightness(1.08);
    }
  }
  &.active {
    .header-button, .show-more, .task-item.tracked {
      background: var(--accent);
      color: var(--on-accent);
      border-color: var(--accent);
      .triangle {
        border-bottom: 10px solid var(--on-accent);
      }
    }
    .header-button:hover, .show-more:hover {
      background: var(--accent-hover);
      color: var(--on-accent);
    }
  }
  &.hidden {
    max-height: 68px;
    transition: max-height 0s 0.3s;
    .expander {
      transform: translateX(calc(-100% + 25px));
      pointer-events: none;
      .show-more {
        pointer-events: auto;
      }
      .show-more .triangle {
        transform: rotate(90deg);
      }
    }
  }
}
.expander {
  pointer-events: all;
  display: flex;
  top: 0%;
  left: 100%;
  z-index: 0;
  transform: translate(0%, 0%);
  transition: transform 0.3s;
  .show-more .triangle {
    transition: 0.3s;
  }

}
.task-item {
  position: relative;
  overflow: hidden;
  justify-content: space-between;
  color: unset;
  &:last-child{
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  .ava {
    position: absolute;
    bottom: 5px;
    right: 5px;
  }
  .probg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    bottom: 0;
    z-index: 0;
    opacity: 0.1;
    object-fit: cover;
    filter: grayscale(1);
  }

  border-left: 1px solid var(--line);
  background: var(--bg2);
  color: var(--text);

  display: flex;
  align-items: center;
  min-height: 68px;

  padding: 0 18px;
  transition: all 0.3s;
  cursor: pointer;
  &:hover {
    filter: brightness(1.2);
  }

  .task-item-info {
    width: max-content;
  }
  .project-stage {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
    span {
      font-size: 12px;
      min-width: fit-content;
    }
  }
  .task-text {
    font-size: 20px;
    font-weight: bold;
    max-width: 220px;
    width: max-content;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tr-open-window {
    font-size: 22px;
    margin-left: 19px;
    transition: 0.3s;
  }
}

.expander-wrapper {
  overflow: hidden;
}

.taskprev {
  height: 68px;
  width: 68px;
  margin-right: -18px;
  display: flex;
  align-items: center;
  img {
    width: 100%;
    pointer-events: none;
  }
  margin-left: 5px;
}

.pause-options {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg2);
  border: 1px solid var(--line);
  border-radius: 0.375rem;
  overflow: hidden;
  box-shadow: 1px 1px 8px var(--shadow-color);
}

.pause-options-enter-active,
.pause-options-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
  transform-origin: top;
}

.pause-options-enter-from,
.pause-options-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.pause-option-item {
  height: 40px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: var(--text);
  white-space: nowrap;
  pointer-events: all;
  font-size: 15px;
  & + & {
    border-top: 1px solid var(--line);
  }
  &:hover {
    background: var(--hover);
    color: var(--accent);
  }
}

.pause-option-icon {
  font-size: 16px;
  opacity: 0.75;
  flex-shrink: 0;
}

.show-more {
  box-shadow: none;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 68px;
  box-sizing: border-box;
  background: var(--neutral);
  color: var(--on-neutral);
  cursor: pointer;
  border-radius: 0 var(--tile-radius) var(--tile-radius) 0;
  border: 0;
  transition: 0.3s;
  &:hover {
    filter: brightness(1.08);
  }
  .triangle {
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid currentColor;
    transform: rotate(-90deg);

  }
}
</style>
